package io.horion.auth.presentation.ui.pages.login

import androidx.compose.runtime.*
import com.varabyte.kobweb.browser.uri.encodeURIComponent
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.icons.fa.FaArrowLeft
import com.varabyte.kobweb.silk.components.icons.fa.FaCircleExclamation
import com.varabyte.kobweb.silk.components.icons.fa.FaEnvelope
import com.varabyte.kobweb.silk.components.icons.fa.FaLock
import com.varabyte.kobweb.silk.components.icons.fa.FaPlus
import com.varabyte.kobweb.silk.components.icons.fa.FaSpinner
import com.varabyte.kobweb.silk.components.icons.fa.FaUser
import com.varabyte.kobweb.silk.components.icons.fa.FaXmark
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.auth.api.models.domain.LoginMethod
import io.horion.auth.di.koinGet
import io.horion.auth.domain.models.AppLanguage
import io.horion.auth.domain.models.Countries
import io.horion.auth.domain.models.LocalStrings
import io.horion.auth.presentation.ui.components.auth.AuthButton
import io.horion.auth.presentation.ui.components.auth.AuthTextField
import io.horion.auth.presentation.ui.components.layouts.PageLayoutData
import io.horion.auth.presentation.ui.pages.navigation.Screen
import io.horion.auth.presentation.ui.styles.AuthContentStyle
import io.horion.auth.presentation.ui.styles.AuthTitleStyle
import io.horion.auth.presentation.ui.styles.LoadingScreen
import io.horion.auth.presentation.ui.styles.PageWrapperStyle
import io.horion.auth.presentation.ui.styles.SpinStyle
import io.horion.auth.presentation.ui.theme.AuthSpacing
import io.horion.auth.presentation.ui.theme.AuthTheme
import io.horion.auth.presentation.ui.theme.AuthTypography
import io.horion.auth.presentation.viewmodel.AuthViewModel
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text
import kotlin.coroutines.cancellation.CancellationException
import kotlin.let

@InitRoute
fun initLoginPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(AppLanguage.strings.pageTitleLogin))
}

@OptIn(DelicateApi::class)
@Page("/")
@Layout("io.horion.auth.presentation.ui.components.layouts.PageLayout")
@Composable
fun loginPage() {
    val ctx = rememberPageContext()
    val coroutineScope = rememberCoroutineScope()
    val colorMode = ColorMode.current
    val colors = AuthTheme.colors(colorMode)
    val redirectParam = ctx.route.queryParams["redirect"]
    val authViewModel = koinGet<AuthViewModel>()

    val currentState = authViewModel.uiState
    val strings = LocalStrings.current

    var isCheckingSession by remember { mutableStateOf(true) }
    var showAccountPicker by remember { mutableStateOf(false) }

    // ── INITIALISATION DE LA SESSION ─────────────────────────────────────────
    LaunchedEffect(Unit) {
        try {
            if (authViewModel.uiState.logoutSucceed) {
                authViewModel.clear()
                isCheckingSession = false
                return@LaunchedEffect
            }

            val isAlreadyConnected = authViewModel.checkSession()
            val currentAccounts = authViewModel.uiState.accounts

            if (isAlreadyConnected && currentAccounts.isNotEmpty()) {
                showAccountPicker = true
            } else {
                authViewModel.clear()
                redirectParam?.let { authViewModel.onChangeRedirect(it) }
            }

        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            authViewModel.clear()
            redirectParam?.let { authViewModel.onChangeRedirect(it) }
        } finally {
            isCheckingSession = false
        }
    }

    // ── TRAITEMENT DE LA REDIRECTION SSO MOBILE OU WEB ────────────────────────
    fun handleAccountSelection(userUuid: String? = null) {
        coroutineScope.launch {
            val destination = redirectParam ?: ""
            val isMobileSso = destination.contains("://callback")

            // Switcher le compte vers celui sélectionné s'il n'est pas actif
            if (userUuid != null) {
                authViewModel.switchAccount(userUuid)
            }

            if (isMobileSso) {
                // 🎯 Génération du code SSO pour le deeplink mobile
                val ssoResult = authViewModel.generateSsoCodeForMobile()
                if (ssoResult != null) {
                    val baseRedirect = destination.substringBefore("?")
                    val separator = if (baseRedirect.contains("?")) "&" else "?"
                    val finalMobileUrl = "$baseRedirect${separator}code=${ssoResult.code}&item=${encodeURIComponent(ssoResult.identifier)}"

                    window.location.href = finalMobileUrl
                    // Déclenche l'ouverture de l'application via le deeplink

                    return@launch
                }
            }

            // Navigation standard sur le Web
            //ctx.router.navigateTo(destination.ifBlank { Screen.Profile.route })
            if (destination.isNotEmpty()) {
                window.location.href = destination
            } else {
                ctx.router.navigateTo(Screen.Profile.route)
            }
        }
    }

    // ── GESTION DE LA REDIRECTION APRÈS NOUVELLE CONNEXION MANUELLE ────────
    LaunchedEffect(currentState.loginSucceed, isCheckingSession) {
        if (isCheckingSession) return@LaunchedEffect

        if (currentState.loginSucceed) {
            val destination = redirectParam ?: ""
            if (destination.contains("://callback")) {
                //val currentActiveAccount = currentState.accounts.firstOrNull { it.isActive }
                //currentActiveAccount?.let { handleAccountSelection(it.userUuid) }
                handleAccountSelection(userUuid = null)
            } else {
                //ctx.router.navigateTo(destination.ifBlank { Screen.Profile.route })
                if (destination.isNotEmpty()) {
                    window.location.href = destination
                } else {
                    ctx.router.navigateTo(Screen.Profile.route)
                }
            }
        }
    }

    val isGlobalLoading = isCheckingSession || currentState.isLoading

    Box(
        modifier = PageWrapperStyle.toModifier(),
        contentAlignment = Alignment.TopCenter
    ) {
        if (isGlobalLoading) {
            LoadingScreen(colors)
        } else if (showAccountPicker && currentState.accounts.isNotEmpty()) {
            // ── ÉCRAN SÉLECTEUR DE COMPTE ────────────────────────────────────────
            Column(
                modifier = AuthContentStyle.toModifier(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Box(modifier = Modifier.height(AuthSpacing.xl))

                SpanText(
                    "Choisir un compte",
                    modifier = AuthTitleStyle.toModifier()
                        .fontWeight(FontWeight.Bold)
                        .color(colors.onBackground)
                )
                Box(modifier = Modifier.height(4.px))
                SpanText(
                    "Pour continuer vers l'application",
                    modifier = Modifier
                        .fontSize(AuthTypography.small)
                        .color(colors.mutedText)
                )
                Box(modifier = Modifier.height(AuthSpacing.xl))

                Column(
                    modifier = Modifier.fillMaxWidth().gap(12.px)
                ) {
                    currentState.accounts.forEach { account ->
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(12.px)
                                .borderRadius(10.px)
                                .border(
                                    width = if (account.isActive) 2.px else 1.px,
                                    style = LineStyle.Solid,
                                    color = if (account.isActive) colors.primary else colors.border
                                )
                                .cursor(Cursor.Pointer)
                                .onClick {
                                    handleAccountSelection(account.userUuid)
                                },
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.gap(12.px)
                            ) {
                                Box(
                                    modifier = Modifier
                                        .size(40.px)
                                        .borderRadius(50.percent)
                                        .background(colors.primary),
                                    contentAlignment = Alignment.Center
                                ) {
                                    FaUser(modifier = Modifier.color(colors.onPrimary))
                                }

                                Column {
                                    SpanText(
                                        account.email.ifBlank { account.phone },
                                        modifier = Modifier
                                            .fontWeight(FontWeight.SemiBold)
                                            .fontSize(AuthTypography.body)
                                            .color(colors.onBackground)
                                    )
                                    SpanText(
                                        if (account.isActive) "Compte actif" else account.role,
                                        modifier = Modifier
                                            .fontSize(AuthTypography.tiny)
                                            .color(if (account.isActive) colors.primary else colors.mutedText)
                                    )
                                }
                            }

                            Box(
                                modifier = Modifier
                                    .padding(8.px)
                                    .cursor(Cursor.Pointer)
                                    .onClick {
                                        authViewModel.logoutAccount(account.userUuid)
                                    }
                            ) {
                                FaXmark(modifier = Modifier.color(colors.mutedText).fontSize(14.px))
                            }
                        }
                    }
                }

                Box(modifier = Modifier.height(AuthSpacing.lg))

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(12.px)
                        .borderRadius(8.px)
                        .border(1.px, LineStyle.Dashed, colors.border)
                        .cursor(Cursor.Pointer)
                        .onClick { showAccountPicker = false },
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center
                ) {
                    FaPlus(modifier = Modifier.color(colors.primary).margin(right = 8.px))
                    SpanText(
                        "Utiliser un autre compte",
                        modifier = Modifier
                            .fontSize(AuthTypography.small)
                            .fontWeight(FontWeight.SemiBold)
                            .color(colors.primary)
                    )
                }

                Box(modifier = Modifier.height(AuthSpacing.xl))
            }
        } else {
            // ── FORMULAIRE DE CONNEXION CLASSIQUE ────────────────────────────────
            Column(
                modifier = AuthContentStyle.toModifier(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (currentState.accounts.isNotEmpty()) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .margin(bottom = 16.px)
                            .cursor(Cursor.Pointer)
                            .onClick { showAccountPicker = true },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        FaArrowLeft(modifier = Modifier.color(colors.primary).margin(8.px))
                        SpanText(
                            "Choisir un compte existant",
                            modifier = Modifier
                                .fontSize(AuthTypography.small)
                                .color(colors.primary)
                                .fontWeight(FontWeight.SemiBold)
                        )
                    }
                }

                Box(modifier = Modifier.height(AuthSpacing.md))

                SpanText(
                    strings.loginTitle,
                    modifier = AuthTitleStyle.toModifier()
                        .fontWeight(FontWeight.Bold)
                        .color(colors.onBackground)
                )
                Box(modifier = Modifier.height(4.px))
                SpanText(
                    strings.loginSubtitle,
                    modifier = Modifier
                        .fontSize(AuthTypography.small)
                        .color(colors.mutedText)
                )
                Box(modifier = Modifier.height(AuthSpacing.xl))

                if (currentState.authErrorMessage != null) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(12.px)
                            .borderRadius(8.px)
                            .border(1.px, LineStyle.Solid, colors.error)
                            .styleModifier { property("background-color", "rgba(239,68,68,0.08)") }
                            .gap(8.px),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        FaCircleExclamation(modifier = Modifier.color(colors.error).fontSize(14.px))
                        SpanText(
                            currentState.authErrorMessage!!,
                            modifier = Modifier.fontSize(AuthTypography.small).color(colors.error)
                        )
                    }
                    Box(modifier = Modifier.height(AuthSpacing.md))
                }

                Row(
                    modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        if (currentState.method == LoginMethod.EMAIL) strings.byEmail else strings.byPhone,
                        modifier = Modifier
                            .fontSize(AuthTypography.small)
                            .fontWeight(FontWeight.Bold)
                            .color(colors.onBackground)
                    )
                    SpanText(
                        if (currentState.method == LoginMethod.EMAIL) strings.switchToPhone else strings.switchToEmail,
                        modifier = Modifier
                            .fontSize(AuthTypography.tiny)
                            .fontWeight(FontWeight.SemiBold)
                            .color(colors.primary)
                            .cursor(Cursor.Pointer)
                            .onClick {
                                val next = if (currentState.method == LoginMethod.EMAIL) LoginMethod.PHONE else LoginMethod.EMAIL
                                authViewModel.clear()
                                authViewModel.onMethodChange(next)
                            }
                    )
                }

                if (currentState.method == LoginMethod.EMAIL) {
                    AuthTextField(
                        modifier = Modifier.fillMaxWidth(),
                        value = currentState.email,
                        onValueChange = { authViewModel.onEmailChange(it) },
                        placeholder = strings.emailPlaceholder,
                        type = InputType.Email,
                        leadingIcon = { FaEnvelope(modifier = Modifier.color(colors.secondary)) },
                        error = currentState.emailError
                    )
                    Box(modifier = Modifier.height(AuthSpacing.sm))
                    AuthTextField(
                        modifier = Modifier.fillMaxWidth(),
                        value = currentState.password,
                        onValueChange = { authViewModel.onPasswordChange(it) },
                        placeholder = strings.passwordPlaceholder,
                        type = InputType.Password,
                        leadingIcon = { FaLock(modifier = Modifier.color(colors.secondary)) },
                        error = currentState.passwordError
                    )
                    Box(modifier = Modifier.height(AuthSpacing.sm))
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                        SpanText(
                            strings.forgotPassword,
                            modifier = Modifier
                                .fontSize(AuthTypography.small)
                                .color(colors.primary)
                                .fontWeight(FontWeight.SemiBold)
                                .cursor(Cursor.Pointer)
                                .onClick { ctx.router.navigateTo(Screen.ResetPassword.route) }
                        )
                    }
                } else {
                    PhoneLoginField(
                        phone = currentState.phone,
                        onPhoneChange = { authViewModel.onPhoneChange(it) },
                        error = currentState.phoneError,
                        colorMode = colorMode,
                        phonePlaceholder = strings.phonePlaceholder
                    )
                    Box(modifier = Modifier.height(AuthSpacing.sm))
                    AuthTextField(
                        modifier = Modifier.fillMaxWidth(),
                        value = currentState.password,
                        onValueChange = { authViewModel.onPasswordChange(it) },
                        placeholder = strings.passwordPlaceholder,
                        type = InputType.Password,
                        leadingIcon = { FaLock(modifier = Modifier.color(colors.secondary)) },
                        error = currentState.passwordError
                    )
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                        SpanText(
                            strings.forgotPassword,
                            modifier = Modifier
                                .fontSize(AuthTypography.small)
                                .color(colors.primary)
                                .fontWeight(FontWeight.SemiBold)
                                .cursor(Cursor.Pointer)
                                .onClick { ctx.router.navigateTo(Screen.ResetPassword.route) }
                        )
                    }
                }

                Box(modifier = Modifier.height(AuthSpacing.xl))

                AuthButton(
                    text = strings.loginButton,
                    onClick = { authViewModel.login() },
                    loading = currentState.isLoading,
                    disabled = if (currentState.method == LoginMethod.EMAIL)
                        currentState.email.isEmpty() || currentState.password.isEmpty()
                    else
                        currentState.phone.isEmpty() || currentState.password.isEmpty()
                )

                Box(modifier = Modifier.height(AuthSpacing.lg))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.Center
                ) {
                    SpanText(
                        strings.noAccount,
                        modifier = Modifier.fontSize(AuthTypography.small).color(colors.mutedText)
                    )
                    SpanText(
                        strings.signUpNow,
                        modifier = Modifier
                            .fontSize(AuthTypography.small)
                            .color(colors.primary)
                            .fontWeight(FontWeight.SemiBold)
                            .cursor(Cursor.Pointer)
                            .onClick { ctx.router.navigateTo(Screen.Register.route) }
                    )
                }
                Box(modifier = Modifier.height(AuthSpacing.xl))
            }
        }
    }
}

//fun loginPage() {
//    val ctx = rememberPageContext()
//    val coroutineScope = rememberCoroutineScope()
//    val colorMode = ColorMode.current
//    val colors = AuthTheme.colors(colorMode)
//    val redirectParam = ctx.route.queryParams["redirect"]
//    val authViewModel = koinGet<AuthViewModel>()
//
//    val currentState = authViewModel.uiState
//    val strings = LocalStrings.current
//
//    var isCheckingSession by remember { mutableStateOf(true) }
//    var showAccountPicker by remember { mutableStateOf(false) }
//
//    LaunchedEffect(Unit) {
//        try {
//            if (authViewModel.uiState.logoutSucceed) {
//                authViewModel.clear()
//                isCheckingSession = false
//                return@LaunchedEffect
//            }
//
//            val isAlreadyConnected = authViewModel.checkSession()
//            val currentAccounts = authViewModel.uiState.accounts
//
//            if (isAlreadyConnected && currentAccounts.isNotEmpty()) {
//                // 🎯 On force l'affichage du sélecteur de compte au lieu de rediriger direct
//                showAccountPicker = true
//            } else {
//                authViewModel.clear()
//                redirectParam?.let { authViewModel.onChangeRedirect(it) }
//            }
//
//        } catch (e: CancellationException) {
//            throw e
//        } catch (e: Exception) {
//            authViewModel.clear()
//            redirectParam?.let { authViewModel.onChangeRedirect(it) }
//        } finally {
//            isCheckingSession = false
//        }
//    }
//
//    // ── FONCTION POUR FINALISER LE DEEPLINK MOBILE SSO ───────────────────────
//    fun handleMobileSsoRedirect(userUuid: String? = null) {
//        coroutineScope.launch {
//            val destination = redirectParam ?: ""
//            val isMobileSso = destination.contains("://callback")
//
//            if (isMobileSso) {
//                // Si changement de compte demandé
//                if (userUuid != null) {
//                    authViewModel.switchAccount(userUuid)
//                }
//
//                // 🎯 Appel de la fonction suspend à l'intérieur de la coroutine
//                val ssoResult = authViewModel.generateSsoCodeForMobile()
//                if (ssoResult != null) {
//                    val baseRedirect = destination.substringBefore("?")
//                    val separator = if (baseRedirect.contains("?")) "&" else "?"
//                    val finalMobileUrl = "$baseRedirect${separator}code=${ssoResult.code}&item=${encodeURIComponent(ssoResult.identifier)}"
//                    window.location.href = finalMobileUrl
//                    return@launch
//                }
//            }
//
//            // Si ce n'est pas du SSO mobile classique
//            ctx.router.navigateTo(destination.ifBlank { Screen.Profile.route })
//        }
//    }
//
//    // ── GESTION DE LA REDIRECTION EN CAS DE CONNEXION RÉUSSIE ─────────────
//    LaunchedEffect(currentState.loginSucceed, isCheckingSession) {
//        if (isCheckingSession) return@LaunchedEffect
//
//        if (currentState.loginSucceed) {
//            handleMobileSsoRedirect()
//        }
//    }
//
////    LaunchedEffect(Unit) {
////        try {
////            if (authViewModel.uiState.logoutSucceed) {
////                authViewModel.clear()
////                isCheckingSession = false
////                return@LaunchedEffect
////            }
////
////            val destination = redirectParam ?: ""
////            val isMobileSso = destination.contains("://callback")
////
////            val isAlreadyConnected = authViewModel.checkSession()
////
////            if (isMobileSso && isAlreadyConnected) {
////                val ssoResult = authViewModel.generateSsoCodeForMobile()
////                if (ssoResult != null) {
////                    val baseRedirect = destination.substringBefore("?")
////                    val separator = if (baseRedirect.contains("?")) "&" else "?"
////                    val finalMobileUrl = "$baseRedirect${separator}code=${ssoResult.code}&item=${ssoResult.identifier}"
////                    window.location.href = finalMobileUrl
////                    return@LaunchedEffect
////                }
////            }
////
////            val currentAccounts = authViewModel.uiState.accounts
////            if (isAlreadyConnected && currentAccounts.isNotEmpty()) {
////                showAccountPicker = true
////            } else {
////                authViewModel.clear()
////                redirectParam?.let { authViewModel.onChangeRedirect(it) }
////            }
////
////        } catch (e: CancellationException) {
////            throw e
////        } catch (e: Exception) {
////            authViewModel.clear()
////            redirectParam?.let { authViewModel.onChangeRedirect(it) }
////        } finally {
////            isCheckingSession = false
////        }
////    }
////
////    LaunchedEffect(currentState.loginSucceed, isCheckingSession) {
////        if (isCheckingSession) return@LaunchedEffect
////
////        if (currentState.loginSucceed) {
////            val destination = currentState.redirect.ifBlank { redirectParam ?: "" }
////
////            try {
////                if (destination.contains("://callback")) {
////                    val ssoResult = if (currentState.codeLogin.isNotBlank()) {
////                        val identifier = if (currentState.method == LoginMethod.EMAIL) currentState.email else currentState.phone
////                        Pair(currentState.codeLogin, identifier)
////                    } else {
////                        val generated = authViewModel.generateSsoCodeForMobile()
////                        if (generated != null) Pair(generated.code, generated.identifier) else null
////                    }
////
////                    if (ssoResult != null) {
////                        val baseRedirect = destination.substringBefore("?")
////                        val separator = if (baseRedirect.contains("?")) "&" else "?"
////                        val finalMobileUrl = "$baseRedirect${separator}code=${ssoResult.first}&item=${ssoResult.second}"
////                        window.location.href = finalMobileUrl
////                    } else {
////                        ctx.router.navigateTo(Screen.Profile.route)
////                    }
////                } else if (destination.isBlank()) {
////                    ctx.router.navigateTo(Screen.Profile.route)
////                } else {
////                    window.location.href = destination
////                }
////            } catch (e: Exception) {
////                ctx.router.navigateTo(Screen.Profile.route)
////            }
////        }
////    }
//
//    // ── VÉRIFICATION ET ÉTAT GLOBAL DU CHARGEMENT ───────────────────────────
//    val isGlobalLoading = isCheckingSession || currentState.isLoading
//
//    Box(
//        modifier = PageWrapperStyle.toModifier(),
//        contentAlignment = Alignment.TopCenter
//    ) {
//        if (isGlobalLoading) {
//            LoadingScreen(colors)
//        } else if (showAccountPicker && currentState.accounts.isNotEmpty()) {
//            // ── ÉCRAN SÉLECTEUR DE COMPTE ────────────────────────────────────────
//            Column(
//                modifier = AuthContentStyle.toModifier(),
//                horizontalAlignment = Alignment.CenterHorizontally
//            ) {
//                Box(modifier = Modifier.height(AuthSpacing.xl))
//
//                SpanText(
//                    "Choisir un compte",
//                    modifier = AuthTitleStyle.toModifier()
//                        .fontWeight(FontWeight.Bold)
//                        .color(colors.onBackground)
//                )
//                Box(modifier = Modifier.height(4.px))
//                SpanText(
//                    "Pour continuer vers l'application",
//                    modifier = Modifier
//                        .fontSize(AuthTypography.small)
//                        .color(colors.mutedText)
//                )
//                Box(modifier = Modifier.height(AuthSpacing.xl))
//
//                Column(
//                    modifier = Modifier.fillMaxWidth().gap(12.px)
//                ) {
//                    currentState.accounts.forEach { account ->
//                        Row(
//                            modifier = Modifier
//                                .fillMaxWidth()
//                                .padding(12.px)
//                                .borderRadius(10.px)
//                                .border(
//                                    width = if (account.isActive) 2.px else 1.px,
//                                    style = LineStyle.Solid,
//                                    color = if (account.isActive) colors.primary else colors.border
//                                )
//                                .cursor(Cursor.Pointer)
//                                .onClick {
//                                    if (account.isActive) {
//                                        val destination = redirectParam ?: Screen.Profile.route
//                                        if (destination.contains("://callback")) {
//                                            authViewModel.login()
//                                        } else {
//                                            ctx.router.navigateTo(destination)
//                                        }
//                                    } else {
//                                        authViewModel.switchAccount(account.userUuid)
//                                    }
//                                },
//                            verticalAlignment = Alignment.CenterVertically,
//                            horizontalArrangement = Arrangement.SpaceBetween
//                        ) {
//                            Row(
//                                verticalAlignment = Alignment.CenterVertically,
//                                modifier = Modifier.gap(12.px)
//                            ) {
//                                Box(
//                                    modifier = Modifier
//                                        .size(40.px)
//                                        .borderRadius(50.percent)
//                                        .background(colors.primary),
//                                    contentAlignment = Alignment.Center
//                                ) {
//                                    FaUser(modifier = Modifier.color(colors.onPrimary))
//                                }
//
//                                Column {
//                                    SpanText(
//                                        account.email.ifBlank { account.phone },
//                                        modifier = Modifier
//                                            .fontWeight(FontWeight.SemiBold)
//                                            .fontSize(AuthTypography.body)
//                                            .color(colors.onBackground)
//                                    )
//                                    SpanText(
//                                        if (account.isActive) "Compte actif" else account.role,
//                                        modifier = Modifier
//                                            .fontSize(AuthTypography.tiny)
//                                            .color(if (account.isActive) colors.primary else colors.mutedText)
//                                    )
//                                }
//                            }
//
//                            Box(
//                                modifier = Modifier
//                                    .padding(8.px)
//                                    .cursor(Cursor.Pointer)
//                                    .onClick {
//                                        authViewModel.logoutAccount(account.userUuid)
//                                    }
//                            ) {
//                                FaXmark(modifier = Modifier.color(colors.mutedText).fontSize(14.px))
//                            }
//                        }
//                    }
//                }
//
//                Box(modifier = Modifier.height(AuthSpacing.lg))
//
//                Row(
//                    modifier = Modifier
//                        .fillMaxWidth()
//                        .padding(12.px)
//                        .borderRadius(8.px)
//                        .border(1.px, LineStyle.Dashed, colors.border)
//                        .cursor(Cursor.Pointer)
//                        .onClick { showAccountPicker = false },
//                    verticalAlignment = Alignment.CenterVertically,
//                    horizontalArrangement = Arrangement.Center
//                ) {
//                    FaPlus(modifier = Modifier.color(colors.primary).margin(right = 8.px))
//                    SpanText(
//                        "Utiliser un autre compte",
//                        modifier = Modifier
//                            .fontSize(AuthTypography.small)
//                            .fontWeight(FontWeight.SemiBold)
//                            .color(colors.primary)
//                    )
//                }
//
//                Box(modifier = Modifier.height(AuthSpacing.xl))
//            }
//        } else {
//            // ── FORMULAIRE DE CONNEXION CLASSIQUE ────────────────────────────────
//            Column(
//                modifier = AuthContentStyle.toModifier(),
//                horizontalAlignment = Alignment.CenterHorizontally
//            ) {
//                if (currentState.accounts.isNotEmpty()) {
//                    Row(
//                        modifier = Modifier
//                            .fillMaxWidth()
//                            .margin(bottom = 16.px)
//                            .cursor(Cursor.Pointer)
//                            .onClick { showAccountPicker = true },
//                        verticalAlignment = Alignment.CenterVertically
//                    ) {
//                        FaArrowLeft(modifier = Modifier.color(colors.primary).margin(8.px))
//                        SpanText(
//                            "Choisir un compte existant",
//                            modifier = Modifier
//                                .fontSize(AuthTypography.small)
//                                .color(colors.primary)
//                                .fontWeight(FontWeight.SemiBold)
//                        )
//                    }
//                }
//
//                Box(modifier = Modifier.height(AuthSpacing.md))
//
//                SpanText(
//                    strings.loginTitle,
//                    modifier = AuthTitleStyle.toModifier()
//                        .fontWeight(FontWeight.Bold)
//                        .color(colors.onBackground)
//                )
//                Box(modifier = Modifier.height(4.px))
//                SpanText(
//                    strings.loginSubtitle,
//                    modifier = Modifier
//                        .fontSize(AuthTypography.small)
//                        .color(colors.mutedText)
//                )
//                Box(modifier = Modifier.height(AuthSpacing.xl))
//
//                if (currentState.authErrorMessage != null) {
//                    Row(
//                        modifier = Modifier
//                            .fillMaxWidth()
//                            .padding(12.px)
//                            .borderRadius(8.px)
//                            .border(1.px, LineStyle.Solid, colors.error)
//                            .styleModifier { property("background-color", "rgba(239,68,68,0.08)") }
//                            .gap(8.px),
//                        verticalAlignment = Alignment.CenterVertically
//                    ) {
//                        FaCircleExclamation(modifier = Modifier.color(colors.error).fontSize(14.px))
//                        SpanText(
//                            currentState.authErrorMessage!!,
//                            modifier = Modifier.fontSize(AuthTypography.small).color(colors.error)
//                        )
//                    }
//                    Box(modifier = Modifier.height(AuthSpacing.md))
//                }
//
//                Row(
//                    modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
//                    horizontalArrangement = Arrangement.SpaceBetween,
//                    verticalAlignment = Alignment.CenterVertically
//                ) {
//                    SpanText(
//                        if (currentState.method == LoginMethod.EMAIL) strings.byEmail else strings.byPhone,
//                        modifier = Modifier
//                            .fontSize(AuthTypography.small)
//                            .fontWeight(FontWeight.Bold)
//                            .color(colors.onBackground)
//                    )
//                    SpanText(
//                        if (currentState.method == LoginMethod.EMAIL) strings.switchToPhone else strings.switchToEmail,
//                        modifier = Modifier
//                            .fontSize(AuthTypography.tiny)
//                            .fontWeight(FontWeight.SemiBold)
//                            .color(colors.primary)
//                            .cursor(Cursor.Pointer)
//                            .onClick {
//                                val next = if (currentState.method == LoginMethod.EMAIL) LoginMethod.PHONE else LoginMethod.EMAIL
//                                authViewModel.clear()
//                                authViewModel.onMethodChange(next)
//                            }
//                    )
//                }
//
//                if (currentState.method == LoginMethod.EMAIL) {
//                    AuthTextField(
//                        modifier = Modifier.fillMaxWidth(),
//                        value = currentState.email,
//                        onValueChange = { authViewModel.onEmailChange(it) },
//                        placeholder = strings.emailPlaceholder,
//                        type = InputType.Email,
//                        leadingIcon = { FaEnvelope(modifier = Modifier.color(colors.secondary)) },
//                        error = currentState.emailError
//                    )
//                    Box(modifier = Modifier.height(AuthSpacing.sm))
//                    AuthTextField(
//                        modifier = Modifier.fillMaxWidth(),
//                        value = currentState.password,
//                        onValueChange = { authViewModel.onPasswordChange(it) },
//                        placeholder = strings.passwordPlaceholder,
//                        type = InputType.Password,
//                        leadingIcon = { FaLock(modifier = Modifier.color(colors.secondary)) },
//                        error = currentState.passwordError
//                    )
//                    Box(modifier = Modifier.height(AuthSpacing.sm))
//                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
//                        SpanText(
//                            strings.forgotPassword,
//                            modifier = Modifier
//                                .fontSize(AuthTypography.small)
//                                .color(colors.primary)
//                                .fontWeight(FontWeight.SemiBold)
//                                .cursor(Cursor.Pointer)
//                                .onClick { ctx.router.navigateTo(Screen.ResetPassword.route) }
//                        )
//                    }
//                } else {
//                    PhoneLoginField(
//                        phone = currentState.phone,
//                        onPhoneChange = { authViewModel.onPhoneChange(it) },
//                        error = currentState.phoneError,
//                        colorMode = colorMode,
//                        phonePlaceholder = strings.phonePlaceholder
//                    )
//                    Box(modifier = Modifier.height(AuthSpacing.sm))
//                    AuthTextField(
//                        modifier = Modifier.fillMaxWidth(),
//                        value = currentState.password,
//                        onValueChange = { authViewModel.onPasswordChange(it) },
//                        placeholder = strings.passwordPlaceholder,
//                        type = InputType.Password,
//                        leadingIcon = { FaLock(modifier = Modifier.color(colors.secondary)) },
//                        error = currentState.passwordError
//                    )
//                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
//                        SpanText(
//                            strings.forgotPassword,
//                            modifier = Modifier
//                                .fontSize(AuthTypography.small)
//                                .color(colors.primary)
//                                .fontWeight(FontWeight.SemiBold)
//                                .cursor(Cursor.Pointer)
//                                .onClick { ctx.router.navigateTo(Screen.ResetPassword.route) }
//                        )
//                    }
//                }
//
//                Box(modifier = Modifier.height(AuthSpacing.xl))
//
//                AuthButton(
//                    text = strings.loginButton,
//                    onClick = { authViewModel.login() },
//                    loading = currentState.isLoading,
//                    disabled = if (currentState.method == LoginMethod.EMAIL)
//                        currentState.email.isEmpty() || currentState.password.isEmpty()
//                    else
//                        currentState.phone.isEmpty() || currentState.password.isEmpty()
//                )
//
//                Box(modifier = Modifier.height(AuthSpacing.lg))
//
//                Row(
//                    modifier = Modifier.fillMaxWidth(),
//                    horizontalArrangement = Arrangement.Center
//                ) {
//                    SpanText(
//                        strings.noAccount,
//                        modifier = Modifier.fontSize(AuthTypography.small).color(colors.mutedText)
//                    )
//                    SpanText(
//                        strings.signUpNow,
//                        modifier = Modifier
//                            .fontSize(AuthTypography.small)
//                            .color(colors.primary)
//                            .fontWeight(FontWeight.SemiBold)
//                            .cursor(Cursor.Pointer)
//                            .onClick { ctx.router.navigateTo(Screen.Register.route) }
//                    )
//                }
//                Box(modifier = Modifier.height(AuthSpacing.xl))
//            }
//        }
//    }
//}


@Composable
private fun PhoneLoginField(
    phone: String,
    onPhoneChange: (String) -> Unit,
    error: String?,
    colorMode: ColorMode,
    phonePlaceholder: String
) {
    val colors = AuthTheme.colors(colorMode)

    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .borderRadius(12.px)
                .backgroundColor(colors.inputBg)
                .border(2.px, LineStyle.Solid, if (error != null) colors.error else colors.border),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Select(attrs = {
                style {
                    property("height", "48px")
                    property("background", "transparent")
                    property("border", "none")
                    property("border-right", "1.5px solid ${colors.borderLight}")
                    property("outline", "none")
                    property("color", colors.onBackground.toString())
                    property("font-size", "13px")
                    property("padding", "0 8px")
                    property("cursor", "pointer")
                    property("min-width", "100px")
                }
            }) {
                Countries.entries.forEach { country -> Option(value = country.name) { Text(country.code) } }
            }
            Input(type = InputType.Tel, attrs = {
                value(phone)
                onInput { onPhoneChange(it.value) }
                attr("placeholder", phonePlaceholder)
                style {
                    property("background", "transparent")
                    property("border", "none")
                    property("outline", "none")
                    property("color", colors.onBackground.toString())
                    property("flex", "1")
                    property("font-size", "14px")
                    property("padding", "0 14px")
                    property("width", "100%")
                    property("height", "48px")
                }
            })
        }
        error?.let {
            SpanText(it, modifier = Modifier.color(colors.error).fontSize(AuthTypography.tiny).padding(top = 6.px, left = 12.px))
        }
    }

}